import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import type { TenantBrand, TenantOrganization } from '../../core/types/data-models';

interface TenantBrandTreeMultiSelectProps {
  value: number[];
  onChange: (ids: number[]) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}

interface FlatNode {
  kind: 'org' | 'brand';
  id: number;
  /** Composite key — orgs and brands can share a numeric id. */
  key: string;
  name: string;
  depth: number;
  /** Org header for a brand row — used to group/label in the dropdown. */
  orgName: string;
  brand?: TenantBrand;
}

function buildFlatTree(orgs: TenantOrganization[], brands: TenantBrand[]): FlatNode[] {
  const brandsByOrg = new Map<number, TenantBrand[]>();
  for (const b of brands) {
    const bucket = brandsByOrg.get(b.organizationId);
    if (bucket) bucket.push(b);
    else brandsByOrg.set(b.organizationId, [b]);
  }
  for (const list of brandsByOrg.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  const sortedOrgs = [...orgs].sort((a, b) => a.name.localeCompare(b.name));

  const result: FlatNode[] = [];
  for (const org of sortedOrgs) {
    result.push({
      kind: 'org',
      id: org.id,
      key: `org-${org.id}`,
      name: org.name,
      depth: 0,
      orgName: org.name,
    });

    const orgBrands = brandsByOrg.get(org.id) ?? [];
    const childrenByParent = new Map<number | null, TenantBrand[]>();
    for (const b of orgBrands) {
      const key = b.parentBrandId ?? null;
      const bucket = childrenByParent.get(key);
      if (bucket) bucket.push(b);
      else childrenByParent.set(key, [b]);
    }

    const visit = (brand: TenantBrand, depth: number) => {
      result.push({
        kind: 'brand',
        id: brand.id,
        key: `brand-${brand.id}`,
        name: brand.name,
        depth,
        orgName: org.name,
        brand,
      });
      for (const child of childrenByParent.get(brand.id) ?? []) {
        visit(child, depth + 1);
      }
    };
    for (const root of childrenByParent.get(null) ?? []) visit(root, 1);
  }

  return result;
}

/**
 * Tenant-scoped sibling of `BrandTreeMultiSelect`. Same UI; the catalog
 * is sourced from `tenant_organization` + `tenant_brand` so the selected
 * ids are `TenantBrand.id` values suitable for the `LineOfBizBrand`
 * junction's `tenantBrandId` slot.
 */
export function TenantBrandTreeMultiSelect({
  value,
  onChange,
  label = 'Brands',
  helperText,
  disabled,
}: TenantBrandTreeMultiSelectProps) {
  const orgs = useQuery({
    queryKey: ['tenant-organizations', 'select'],
    queryFn: () => resources.tenantOrganizations.list({ pageSize: 500, sort: 'name' }),
  });
  const brands = useQuery({
    queryKey: ['tenant-brands', 'select'],
    queryFn: () => resources.tenantBrands.list({ pageSize: 1000, sort: 'name' }),
  });

  const flat = useMemo(
    () => buildFlatTree(orgs.data?.data ?? [], brands.data?.data ?? []),
    [orgs.data, brands.data],
  );

  const brandNodesById = useMemo(() => {
    const map = new Map<number, FlatNode>();
    for (const n of flat) if (n.kind === 'brand') map.set(n.id, n);
    return map;
  }, [flat]);

  const selected = useMemo(
    () =>
      value
        .map((id) => brandNodesById.get(id))
        .filter((n): n is FlatNode => n != null),
    [value, brandNodesById],
  );

  const loading = orgs.isLoading || brands.isLoading;

  return (
    <Autocomplete<FlatNode, true, false, false>
      multiple
      fullWidth
      disabled={disabled}
      options={flat}
      value={selected}
      onChange={(_, next) =>
        onChange(next.filter((n) => n.kind === 'brand').map((n) => n.id))
      }
      getOptionDisabled={(o) => o.kind === 'org'}
      getOptionLabel={(o) =>
        o.kind === 'brand' ? `${o.orgName} / ${o.name}` : o.name
      }
      isOptionEqualToValue={(a, b) => a.key === b.key}
      loading={loading}
      disableCloseOnSelect
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.key}
          sx={{
            pl: 1.5 + option.depth * 2,
            opacity: option.kind === 'org' ? 0.85 : 1,
            backgroundColor: option.kind === 'org' ? 'action.hover' : undefined,
            pointerEvents: option.kind === 'org' ? 'none' : undefined,
          }}
        >
          {option.kind === 'org' ? (
            <>
              <BusinessIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {option.name}
              </Typography>
            </>
          ) : (
            <>
              {option.depth > 1 && (
                <Typography component="span" sx={{ color: 'text.disabled', mr: 1 }}>
                  {'└─'}
                </Typography>
              )}
              <StorefrontIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="body2">{option.name}</Typography>
            </>
          )}
        </Box>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={key}
              size="small"
              icon={<StorefrontIcon fontSize="small" />}
              label={`${option.orgName} / ${option.name}`}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          placeholder={selected.length === 0 ? 'Select brands…' : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
